import { Theme } from "@mui/material";
import Icon from "@mui/material/Icon";
import Guid from "common/values/guid/guid";
import Individual from "marketplace/entities/individual/individual";
import Permission from "permissions/entities/permission/permission";
import { Resource } from "permissions/entities/permission/resources/general";
import GroupedPermissionCard from "permissions/entities/permission/view/components/permissions-table/components/grouped-permission-card";
import GroupedTemplatePermissionCard from "permissions/entities/permission/view/components/permissions-table/components/grouped-template-permission-card";
import SinglePermissionCard from "permissions/entities/permission/view/components/permissions-table/components/single-permission-card";
import SingleTemplatePermissionCard from "permissions/entities/permission/view/components/permissions-table/components/single-template-permission-card";
import {
  CardType,
  CategoryLabel,
  ControlMeta,
  TableRow,
  UniversalCellConfig,
} from "permissions/entities/permission/view/components/permissions-table/types";
import { isPermissionsRow, isTemplateRow } from "permissions/entities/permission/view/components/permissions-table/utils";
import { JSX } from "react";

type PermissionCardProps = {
  row: TableRow | null;
  config: UniversalCellConfig;
  isSingleGranteeView: boolean;
  isCreateTemplateView: boolean;
  isManageTemplatesView: boolean;
  theme: Theme;
  activeCategory: CategoryLabel | "";
  onTogglePermission(permission: Permission | undefined): void;
  onToggleTemplate(resource: Resource | undefined): void;
  onApplyPermissionMeta(
    effective: Permission,
    resource: Resource,
    meta: ControlMeta,
    newValue: string
  ): void;
  onApplyTemplateMeta(
    resource: Resource,
    meta: ControlMeta,
    newValue: string
  ): void;
  individuals: Individual[];
  loadingIndividualUserIds: Guid[];
  getCardSubheader(resource: Resource): string | undefined;
}

export default function PermissionCard(
  props: Readonly<PermissionCardProps>): JSX.Element {
  const {
    row,
    config,
    isSingleGranteeView,
    isCreateTemplateView,
    isManageTemplatesView,
    theme,
    activeCategory,
    onTogglePermission,
    onToggleTemplate,
    onApplyPermissionMeta,
    onApplyTemplateMeta,
    individuals,
    loadingIndividualUserIds,
    getCardSubheader
  } = props;

  if (config.cardType === CardType.Single) {
    if (isManageTemplatesView && isTemplateRow(row)) {
      const resource = config.candidates(row, config.resource)[0] as Resource | undefined;
      return (
        <SingleTemplatePermissionCard
          canEdit={config.canEdit || !row?.id}
          resource={resource ?? config.template?.(config.resource)}
          config={config}
          theme={theme}
          activeCategory={activeCategory}
          getCardSubheader={getCardSubheader}
          onToggle={onToggleTemplate}
          onApplyMeta={onApplyTemplateMeta}
        />
      );
    }

    if (isPermissionsRow(row)) {
      return (
        <SinglePermissionCard
          row={row}
          config={config}
          isSingleGranteeView={isSingleGranteeView}
          isCreateTemplateView={isCreateTemplateView}
          isManageTemplatesView={isManageTemplatesView}
          theme={theme}
          activeCategory={activeCategory}
          individuals={individuals}
          loadingIndividualUserIds={loadingIndividualUserIds}
          getCardSubheader={getCardSubheader}
          onToggle={onTogglePermission}
          onApplyMeta={onApplyPermissionMeta}
        />
      );
    }
  }
  if (config.cardType === CardType.Grouped) {
    if (isManageTemplatesView && isTemplateRow(row)) {
      return (
        <GroupedTemplatePermissionCard
          row={row}
          canEdit={config.canEdit || !row?.id}
          config={config}
          theme={theme}
          activeCategory={activeCategory}
          getCardSubheader={getCardSubheader}
          onToggle={onToggleTemplate}
          onApplyMeta={onApplyTemplateMeta}
        />
      );
    }

    if (isPermissionsRow(row)) {
      return (
        <GroupedPermissionCard
          row={row}
          config={config}
          isSingleGranteeView={isSingleGranteeView}
          isCreateTemplateView={isCreateTemplateView}
          isManageTemplatesView={isManageTemplatesView}
          theme={theme}
          activeCategory={activeCategory}
          getCardSubheader={getCardSubheader}
          onToggle={onTogglePermission}
          onApplyMeta={onApplyPermissionMeta}
        />
      );
    }
  }
  
  return <Icon>remove</Icon>;
}
